import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import Logo from "./Logo";
import NavBar from "./NavBar";
import FloatingActionShortcuts from "./FloatingActionShortcuts";
import { useMediaQuery } from "../../../hooks/useMediaQuery"
import { VisibilityMode } from "../types";
import { VisibilityModeCtx } from "../contexts/visibilityModeCtx";
import { getStoredVisibilityMode, persistVisibilityMode } from "../hooks/useNavbarDisplayMode";

export default function Header() {
	const navBarRef = useRef<HTMLDivElement>(null);
	const { scrollY } = useScroll();
	const [visibilityMode, setVisibilityMode] = useState<VisibilityMode | null>(null);
	const [showHeader, setShowHeader] = useState(true);
	const [lastScrollY, setLastScrollY] = useState(0);
	const isMobile = useMediaQuery ("(max-width: 768px)");

	const initializeNavbarDisplayMode = () => {
		const mode = getStoredVisibilityMode();
		setVisibilityMode(mode);
		persistVisibilityMode(mode);		
	};

	useEffect(() => {
        initializeNavbarDisplayMode();
    }, []);

	useMotionValueEvent(scrollY, "change", (currentY) => {
		if (visibilityMode !== VisibilityMode.AutoHide) return; // 🚫 
		
		const navBar = navBarRef.current?.getBoundingClientRect().height ?? 0;
		if (currentY <= navBar/2.7) return; // 🚫 

		if (currentY > lastScrollY) {
			setShowHeader(false); // ↓ scroll down/hide
		} else {
			setShowHeader(true); // ↑ scroll up/show
		}

		setLastScrollY(currentY);
	});

	useEffect(() => {
		if (visibilityMode === VisibilityMode.AutoHide && !showHeader) {
			setShowHeader(true);
		}		
	}, [visibilityMode]);


	if (visibilityMode === null) {
		return (
			<div
				role="presentation"
				aria-hidden="true"
				className="w-screen min-w-screen flex justify-center items-center bg-transparent min-h-23 h-23 xs:h-30 lg:h-40 2xl:h-[12vw]"
			/>
		)
	}

	return (
		<VisibilityModeCtx.Provider value={{visibilityMode, setVisibilityMode}}>
			<header
				className={`w-screen min-w-screen flex justify-center items-center bg-transparent min-h-23 h-23 xs:h-30 lg:h-40 2xl:h-[12vw]`}
				id="home"
				ref={navBarRef}
			>
				<motion.div
					animate={{ 
						y: isMobile 
							? 0 
							: visibilityMode === VisibilityMode.AutoHide
								? showHeader 
									? 0 
									: "-100%" 
								: 0
					}}
					transition={{ duration: 0.7, ease: "easeOut" }}
					className={`
						w-[90vw] h-30 lg:h-40 2xl:h-[12vw] md:z-1000 md:top-0 md:-translate-x-[50vw] md:left-[50vw] md:px-[5vw] md:min-w-screen
						${(visibilityMode === VisibilityMode.AutoHide || visibilityMode === VisibilityMode.Fixed)
							? "md:fixed md:bg-gradient-to-b md:from-black/75 md:to-black/0 md:[backdrop-filter:blur(1px)]" 
							: "md:relative" 
						}
						flex justify-between items-center overflow-visible 
					`}
				>
					<Logo />
					<NavBar showHeader={showHeader} />
				</motion.div>

				<FloatingActionShortcuts />
			</header>
		</VisibilityModeCtx.Provider>
	);
}